import { useState, useEffect } from 'react';
import { Panel, Seg, Toggle, Tag, Empty, FinTable, nf, par, pc, mx, NumField, Insp } from '../ui.jsx';
import { RevenueChart, Bridge } from '../charts.jsx';
import { contribution, LEVELS } from '../engine/contribution.js';
import { lineOf, accName, BS_ASSETS, BS_LIABS } from '../engine/accounts.js';
import { flow } from '../engine/ledger.js';
import { useSaveContributionClassificationsMutation, useGetAddbacksQuery, useSaveAddbacksMutation, useGetDepreciationOverridesQuery, useSaveDepreciationOverridesMutation } from '../../slices/ValuatorProSlice.js';

const LINE_LABEL = {};
[...BS_ASSETS, ...BS_LIABS].forEach(s => s.lines.forEach(l => { LINE_LABEL[l.k] = l.n; }));

const noData = <div className="left"><Empty title="Tablo üretmek için veri yok">L1'de mizan ya da beyanname yükleyin.</Empty></div>;

export function L3Balance({ hist, st, up, print }){
  const [cur, setCur] = useState('TL');
  const [modalKey, setModalKey] = useState(null);
  useEffect(() => {
    if (!modalKey) return;
    const onKey = e => { if (e.key === 'Escape') setModalKey(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalKey]);
  if (!hist.length) return noData;
  const displayCur = print ? 'USD' : cur;
  const conv = (h, v) => displayCur==='TL' ? v/1000 : v/(h.fx.end||1)/1000;
  const head = hist.map(h=>({ l:String(h.year) }));
  const rows = [];
  hist[0].bs.secs.forEach((s, si) => {
    if (si===2) rows.push({ l:'TOPLAM AKTİF', v: hist.map(h=>conv(h,h.bs.TA)), cls:'sum' }, { spacer:true });
    rows.push({ l:s.sec, v: hist.map(h=>conv(h,h.bs.secs[si].total)), cls:'sub' });
    s.lines.forEach((l, li) => {
      const vals = hist.map(h=>conv(h,h.bs.secs[si].lines[li].v));
      if (vals.some(x=>Math.abs(x)>0.05)) rows.push({ l: l.n, v: vals, cls:'kid', key: l.k, tag: hist.some(h=>h.bs.secs[si].lines[li].computed) ? <Tag c="gold">hesaplandı</Tag> : null });
    });
  });
  rows.push({ l:'TOPLAM PASİF', v: hist.map(h=>conv(h,h.bs.TP)), cls:'sum' });
  rows.push({ l:'Denklik farkı', v: hist.map(h=>conv(h,h.bs.check)), cls:'dim' });

  const EQ_PARTS = [{k:'paidIn'},{k:'capRes'},{k:'profitRes'},{k:'prevProfit'},{k:'prevLoss'},{k:'netProfit'}];
  const vrows = [
    { l:'Ticari alacaklar + diğer alacaklar', v: hist.map(h=>conv(h,h.bs.v.tradeRec+h.bs.v.otherRec)), cls:'kid', parts:[{k:'tradeRec'},{k:'otherRec'}] },
    { l:'Stoklar ve verilen avanslar', v: hist.map(h=>conv(h,h.bs.v.inv+h.bs.v.advGiven)), cls:'kid', parts:[{k:'inv'},{k:'advGiven'}] },
    { l:'Diğer faaliyet varlıkları', v: hist.map(h=>conv(h,h.bs.v.prepaid+h.bs.v.otherCA+h.bs.v.constr)), cls:'kid', parts:[{k:'prepaid'},{k:'otherCA'},{k:'constr'}] },
    { l:'Faaliyet borçları (−)', v: hist.map(h=>-conv(h,h.bs.opCL)), cls:'kid', parts:[{k:'tradePay',s:-1},{k:'otherPayST',s:-1},{k:'advRecv',s:-1},{k:'constrPr',s:-1},{k:'taxPay',s:-1},{k:'provST',s:-1},{k:'defIncST',s:-1},{k:'otherSTL',s:-1}] },
    { l:'Net işletme sermayesi', v: hist.map(h=>conv(h,h.bs.nwc)), cls:'sub' },
    { spacer:true },
    { l:'Mali borçlar (KV + UV)', v: hist.map(h=>conv(h,h.bs.v.finDebtST+h.bs.v.finDebtLT)), cls:'kid', parts:[{k:'finDebtST'},{k:'finDebtLT'}] },
    { l:'Ortaklara borçlar', v: hist.map(h=>conv(h,h.bs.partners)), cls:'kid', tag: st.partnersAsDebt!==false ? <Tag>borç</Tag> : <Tag c="plum">özkaynak benzeri</Tag>, parts:[{k:'partnersST'},{k:'partnersLT'}] },
    { l:'Nakit ve menkul kıymetler (−)', v: hist.map(h=>-conv(h,h.bs.cash)), cls:'kid', parts:[{k:'cash',s:-1},{k:'secs',s:-1}] },
    { l:'Net finansal borç', v: hist.map(h=>conv(h,h.bs.netDebt)), cls:'sub' },
    { spacer:true },
    { l:'Yatırılmış sermaye (özkaynak + net borç)', v: hist.map(h=>conv(h,h.bs.investedCapital)), cls:'hero' },
    { l:'Net aktifler (aktif − yabancı kaynak)', v: hist.map(h=>conv(h,h.bs.EQ)), cls:'sub', parts:EQ_PARTS },
    { l:'Maddi net aktifler (MODV hariç)', v: hist.map(h=>conv(h,h.bs.tangibleEquity)), cls:'kid', parts:[...EQ_PARTS,{k:'intang',s:-1}] },
  ];
  const K = k => hist.map(h=>h.kpi[k]);
  const krows = [
    { l:'Cari oran', v:K('current'), f:x=>mx(x,2), formula:'Dönen Varlıklar / Kısa Vadeli Yabancı Kaynaklar', ratioParts: h=>[{l:'Dönen Varlıklar',v:h.bs.CA},{l:'Kısa Vadeli Yabancı Kaynaklar',v:h.bs.STL}], f2:x=>mx(x,2) },
    { l:'Asit test oranı', v:K('quick'), f:x=>mx(x,2), formula:'(Dönen Varlıklar − Stoklar) / Kısa Vadeli Yabancı Kaynaklar', ratioParts: h=>[{l:'Dönen Varlıklar − Stoklar',v:h.bs.CA-h.bs.v.inv},{l:'Kısa Vadeli Yabancı Kaynaklar',v:h.bs.STL}], f2:x=>mx(x,2) },
    { l:'Alacak tahsil süresi (gün)', v:K('dso'), f:x=>nf(x), formula:'Ticari Alacaklar / Net Satışlar × 365', ratioParts: h=>[{l:'Ticari Alacaklar',v:h.bs.v.tradeRec},{l:'Net Satışlar',v:h.is.netSales}], f2:x=>nf(x)+' gün' },
    { l:'Stok devir süresi (gün)', v:K('dio'), f:x=>nf(x), formula:'Stoklar / Satışların Maliyeti × 365', ratioParts: h=>[{l:'Stoklar',v:h.bs.v.inv},{l:'Satışların Maliyeti',v:h.is.cogs}], f2:x=>nf(x)+' gün' },
    { l:'Borç ödeme süresi (gün)', v:K('dpo'), f:x=>nf(x), formula:'Ticari Borçlar / Satışların Maliyeti × 365', ratioParts: h=>[{l:'Ticari Borçlar',v:h.bs.v.tradePay},{l:'Satışların Maliyeti',v:h.is.cogs}], f2:x=>nf(x)+' gün' },
    { l:'Nakit dönüşüm süresi (gün)', v:K('ccc'), f:x=>nf(x), cls:'sub', formula:'Alacak tahsil + Stok devir − Borç ödeme (gün)' },
    { l:'Stok / net satış', v:K('invS'), f:pc, cls: hist.some(h=>h.kpi.invS>0.2)?'warnrow':'', formula:'Stoklar / Net Satışlar', ratioParts: h=>[{l:'Stoklar',v:h.bs.v.inv},{l:'Net Satışlar',v:h.is.netSales}], f2:pc },
    { l:'NİS / net satış', v:K('nwcS'), f:pc, formula:'Net İşletme Sermayesi / Net Satışlar', ratioParts: h=>[{l:'Net İşletme Sermayesi',v:h.bs.nwc},{l:'Net Satışlar',v:h.is.netSales}], f2:pc },
    { l:'Net borç / FAVÖK', v:K('ndEbitda'), f:x=>mx(x,1), formula:'Net Finansal Borç / Normalize FAVÖK', ratioParts: h=>[{l:'Net Finansal Borç',v:h.bs.netDebt},{l:'Normalize FAVÖK',v:h.ebitdaAdj}], f2:x=>mx(x,1) },
    { l:'Faiz karşılama (FVÖK / finansman)', v:K('cover'), f:x=>mx(x,1), formula:'FVÖK / Finansman Giderleri', ratioParts: h=>[{l:'FVÖK',v:h.is.ebit},{l:'Finansman Giderleri',v:h.is.finExp}], f2:x=>mx(x,1) },
    { l:'Yabancı kaynak / aktif', v:K('lev'), f:pc, formula:'(KVYK + UVYK) / Toplam Aktif', ratioParts: h=>[{l:'KVYK + UVYK',v:h.bs.STL+h.bs.LTL},{l:'Toplam Aktif',v:h.bs.TA}], f2:pc },
    { l:'Özkaynak kârlılığı', v:K('roe'), f:pc, formula:'Net Dönem Kârı / Özkaynaklar', ratioParts: h=>[{l:'Net Dönem Kârı',v:h.is.net},{l:'Özkaynaklar',v:h.bs.EQ}], f2:pc },
  ];
  const unit = displayCur==='TL' ? 'bin TL' : "bin USD, yıl sonu kuru";
  const last = hist[hist.length-1];
  const body = (<>
    <Panel title="Bilanço özeti" sub={unit} right={!print && <Seg small value={cur} onChange={setCur} options={[{v:'TL',l:'TL'},{v:'USD',l:'USD'}]} />} flush className="print-narrow">
      <FinTable head={head} rows={rows} firstLabel="" onRowClick={r=>setModalKey(r)} note={cur==='USD' ? 'Bilanço kalemleri yıl sonu kuruyla çevrildi: '+hist.map(h=>`${h.year} ${nf(h.fx.end,4)}`).join(', ')+'.' : 'Tekdüzen hesap planı gruplarıyla, mizan net bakiyelerinden üretildi.'} />
    </Panel>
    <div className="print-page">
      <Panel title="Değerlemeye giden bilanço kalemleri" sub={unit} flush className="print-narrow"><FinTable head={head} rows={vrows} onRowClick={r=>setModalKey(r)} /></Panel>
      <Panel title="Finansal oranlar" flush className="print-narrow"><FinTable head={head} rows={krows} fmt={x=>nf(x,2)} onRowClick={r=>setModalKey(r)} /></Panel>
    </div>
  </>);
  if (print) return <div className="left">{body}</div>;

  const modal = modalKey && (
    <div style={{ position:'fixed', inset:0, background:'rgba(17,28,51,.55)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center' }} onClick={()=>setModalKey(null)}>
      <div style={{ background:'#fff', borderRadius:10, width:'min(960px, 95vw)', maxHeight:'80vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,.3)', position:'relative' }} onClick={e=>e.stopPropagation()}>
        <button type="button" onClick={()=>setModalKey(null)} aria-label="Kapat"
          style={{ position:'absolute', top:10, right:12, border:0, background:'none', fontSize:20, lineHeight:1, cursor:'pointer', color:'var(--muted)' }}>×</button>
        <div style={{ padding:'18px 22px' }}>
          <h3 style={{ margin:'0 0 4px' }}>{modalKey.l}</h3>
          {modalKey.formula && <p className="muted small" style={{ margin:'0 0 14px' }}>{modalKey.formula}</p>}
          {modalKey.ratioParts ? (() => {
            const labels = modalKey.ratioParts(hist[0]).map(p=>p.l);
            return (
              <table className="g"><thead><tr className="hd solo"><th className="lbl">Bileşen</th>{hist.map(h=><th key={h.id} className="n">{h.year}</th>)}</tr></thead>
                <tbody>
                  {labels.map((lab, li) => (
                    <tr key={lab}><td className="lbl">{lab}</td>
                      {hist.map(h => <td key={h.id} className="n">{par(conv(h, modalKey.ratioParts(h)[li].v))}</td>)}
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr className="sum"><td className="lbl">Oran</td>
                  {hist.map((h,hi) => <td key={h.id} className="n">{(modalKey.f2||modalKey.f)(modalKey.v[hi])}</td>)}
                </tr></tfoot>
              </table>
            );
          })() : modalKey.parts ? (() => {
            const parts = modalKey.parts;
            return (
              <table className="g"><thead><tr className="hd solo"><th className="lbl">Kalem</th>{hist.map(h=><th key={h.id} className="n">{h.year}</th>)}</tr></thead>
                <tbody>
                  {parts.map(p => (
                    <tr key={p.k}><td className="lbl">{LINE_LABEL[p.k] || p.k}</td>
                      {hist.map(h => <td key={h.id} className="n">{par((p.s===-1?-1:1)*conv(h,h.bs.v[p.k]||0))}</td>)}
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr className="sum"><td className="lbl">Toplam</td>
                  {hist.map(h => <td key={h.id} className="n">{par(parts.reduce((s,p)=>s+(p.s===-1?-1:1)*conv(h,h.bs.v[p.k]||0),0))}</td>)}
                </tr></tfoot>
              </table>
            );
          })() : (() => {
            const byCode = {};
            hist.forEach(h => {
              Object.values(h.L.acc).forEach(a => {
                if (lineOf(a.code)?.line.k !== modalKey.key) return;
                (byCode[a.code] ||= { name: a.name || accName(a.code), vals: {} }).vals[h.year] = a.dt - a.ct;
              });
            });
            const codes = Object.keys(byCode).sort();
            if (!codes.length) return <p className="muted small">Bu yıllarda bu satırı besleyen hesap yok.</p>;
            return (
              <table className="g"><thead><tr className="hd solo"><th className="lbl">Hesap</th>{hist.map(h=><th key={h.id} className="n">{h.year}</th>)}</tr></thead>
                <tbody>
                  {codes.map(code => (
                    <tr key={code}><td className="lbl"><code>{code}</code> {byCode[code].name}</td>
                      {hist.map(h => <td key={h.id} className="n">{par(conv(h, byCode[code].vals[h.year] ?? 0))}</td>)}
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr className="sum"><td className="lbl">Toplam</td>
                  {hist.map(h => <td key={h.id} className="n">{par(conv(h, codes.reduce((s,cc)=>s+(byCode[cc].vals[h.year]??0),0)))}</td>)}
                </tr></tfoot>
              </table>
            );
          })()}
        </div>
      </div>
    </div>
  );
  return (<>
    <div className="left">{body}</div>
    {modal}
    <Insp>
      <div className="ihead">Uyarlamalar <span>L3</span></div>
      <div className="ihero"><div className="big">{nf(last.usd.ic/1000,2)} M$</div><div className="lb">{last.year} yatırılmış sermaye</div>
        <div className="sub">Özkaynak {nf(last.usd.equity/1000,2)} M$, net borç {nf(last.usd.netDebt/1000,2)} M$. Net aktifler değerleme aralığının tabanını oluşturur.</div></div>
      <div className="iblk"><h4>Sınıflama</h4>
        <Toggle checked={st.partnersAsDebt!==false} onChange={v=>up({partnersAsDebt:v})} title="Ortaklara borçları net borca kat" desc="Kapatılırsa 331 ve 431 hesapları özkaynak benzeri kabul edilir; firma değeri değişmez, özkaynak değeri artar." />
      </div>
      <div className="iblk"><h4>{last.year} NİS bileşimi (bin USD)</h4>
        <div className="ical">
          <span className="k">Ticari alacaklar</span><span className="v">{nf(last.usd.tradeRec)}</span>
          <span className="k">Stoklar</span><span className="v">{nf(last.usd.inv)}</span>
          <span className="k">Ticari borçlar</span><span className="v">({nf(last.usd.tradePay)})</span>
          <span className="k tot">Net işletme sermayesi</span><span className="v tot">{nf(last.usd.nwc)}</span>
        </div>
      </div>
      <div className="inote">Stok / ciro {pc(last.kpi.invS)}. {last.kpi.invS>0.2 ? <b>%20 hedefinin üzerinde; L5'te NİS oranını düşürmek nakit akımını doğrudan artırır.</b> : 'Hedef aralıkta.'}</div>
    </Insp>
  </>);
}

export function L4Income({ hist, st, up, print }){
  const [mode, setMode] = useState('USD');
  const [cy, setCy] = useState(hist.length ? hist[hist.length-1].year : null);
  const [modalKey, setModalKey] = useState(null);
  const [abDraft, setAbDraft] = useState(null);
  const [abHint, setAbHint] = useState(null);
  const [daTouched, setDaTouched] = useState(false);
  const [daConfirm, setDaConfirm] = useState(false);
  useEffect(() => {
    if (!modalKey) return;
    const onKey = e => { if (e.key === 'Escape') setModalKey(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalKey]);
  const [saveClassifications, { isLoading: savingCls }] = useSaveContributionClassificationsMutation();
  const Hpre = hist.find(h=>h.year===cy) || hist[hist.length-1];
  const ctPre = hist.length ? contribution(Hpre, st.contrib) : { rows: [] };
  const { data: addbacksData } = useGetAddbacksQuery(st.valuationTaskId, { skip: !st.valuationTaskId });
  const [saveAddbacksMut, { isLoading: savingAb }] = useSaveAddbacksMutation();
  const { data: daOverrideData } = useGetDepreciationOverridesQuery(st.valuationTaskId, { skip: !st.valuationTaskId });
  const [saveDaOverrideMut] = useSaveDepreciationOverridesMutation();
  useEffect(() => { if (daOverrideData) up({ daOverride: Object.fromEntries(daOverrideData.map(r => [r.year, r.amount])) }); }, [daOverrideData]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (addbacksData) up({ addbacks: addbacksData.map(a => ({ id: a.id, description: a.description || a.name || '', year: a.year, tl: a.amount, currency: a.currency, ev: a.hasEvidence, on: a.isActive })) }); }, [addbacksData]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!hist.length) return noData;
  const displayMode = print ? 'USD' : mode;
  const c = (h, v) => displayMode==='TL' ? v/1000 : displayMode==='USD' ? v/(h.fx.avg||1)/1000 : v/(h.is.netSales||1);
  const fmt = mode==='PCT' ? (x=>pc(x)) : par;
  const head = hist.map(h=>({ l: String(h.year)+(h.months<12?` (${h.months} ay)`:'') }));
  const R = (l, f, cls, tag, codes) => ({ l, v: hist.map(h=>c(h, f(h))), cls, tag, codes });
  const rows = [
    R('Brüt satışlar', h=>h.is.grossSales, 'kid', null, [{from:600,to:602,s:1}]),
    R('Satış indirimleri (−)', h=>-h.is.deductions, 'kid', null, [{from:610,to:612,s:-1}]),
    R('Net satışlar', h=>h.is.netSales, 'sub'),
    R('Satışların maliyeti (−)', h=>-h.is.cogs, 'kid', null, [{from:620,to:623,s:-1}]),
    R('Brüt kâr', h=>h.is.gross, 'sub'),
    R('Araştırma ve geliştirme (−)', h=>-h.is.rnd, 'kid', null, [{from:630,to:630,s:-1}]),
    R('Pazarlama, satış ve dağıtım (−)', h=>-h.is.psd, 'kid', null, [{from:631,to:631,s:-1}]),
    R('Genel yönetim (−)', h=>-h.is.ga, 'kid', null, [{from:632,to:632,s:-1}]),
    R('Faaliyet kârı (FVÖK)', h=>h.is.ebit, 'sub'),
    R('Amortisman ve itfa (+)', h=>h.is.da.total, 'kid'),
    R('FAVÖK', h=>h.is.ebitda, 'sub'),
    R('Geri eklemeler (+)', h=>h.addb, 'kid'),
    ...(st.fxInEbitda ? [R('Net kur farkı (+/−)', h=>h.fxInc, 'kid')] : []),
    R('Normalize FAVÖK', h=>h.ebitdaAdj, 'hero'),
    { l:'Normalize FAVÖK marjı', v: hist.map(h=>h.ebitdaAdj/(h.is.netSales||1)), f:pc, cls:'dim' },
    { spacer:true },
    R('Diğer faaliyet gelirleri', h=>h.is.otherInc, 'kid', null, [{from:640,to:649,s:1}]),
    R('Diğer faaliyet giderleri (−)', h=>-h.is.otherExp, 'kid', null, [{from:650,to:659,s:-1}]),
    R('Finansman giderleri (−)', h=>-h.is.finExp, 'kid', null, [{from:660,to:661,s:-1}]),
    R('Olağandışı gelir ve giderler (net)', h=>h.is.extraInc-h.is.extraExp, 'kid', null, [{from:671,to:671,s:1},{from:679,to:679,s:1},{from:680,to:681,s:-1},{from:689,to:689,s:-1}]),
    R('Vergi öncesi kâr', h=>h.is.ebt, 'sub'),
    R('Vergi karşılığı (−)', h=>-h.is.tax, 'kid', null, [{from:691,to:691,s:-1}]),
    R('Net dönem kârı', h=>h.is.net, 'sum'),
  ];
  const H = Hpre;
  const ct = ctPre;
  const setCls = (d, v) => up(s => ({ contrib: { ...s.contrib, [d]: v } }));
  const lastU = hist[hist.length-1].usd;
  const recon = hist.map(h => { const ms = st.monthly.filter(m=>m.y===h.year); return ms.length===12 ? { y:h.year, rep: ms.reduce((s,m)=>s+m.rev,0), miz: h.is.netSales, mat: ms.reduce((s,m)=>s+m.mat,0), mat7: h.is.cost7.mat } : null; }).filter(Boolean);
  const unit = displayMode==='TL' ? 'bin TL' : displayMode==='USD' ? 'bin USD, yıl ortalaması kuru' : 'net satışlara oran';

  const body = (<>
    <Panel title="Normalize gelir tablosu" sub={unit} right={!print && <Seg small value={mode} onChange={setMode} options={[{v:'TL',l:'TL'},{v:'USD',l:'USD'},{v:'PCT',l:'% ciro'}]} />} flush className="print-narrow">
      <FinTable head={head} rows={rows} fmt={fmt} onRowClick={r=>setModalKey(r)} note="Gelir tablosu 6'lı hesaplardan, amortisman 7'li hesapların 'amortisman' alt hesaplarından okunur. Geri eklemeler FAVÖK'ün içine karıştırılmaz, kendi satırında durur." />
    </Panel>
    <Panel title="Ciro ve FAVÖK marjı" sub="USD" className="print-narrow print-chart-sm"><RevenueChart data={hist.map(h=>({ l:String(h.year), rev:h.usd.rev, margin:h.kpi.ebitdaM }))} /></Panel>
    <div className="print-page">
    <div className="two">
      <Panel title={`${hist[hist.length-1].year} FAVÖK köprüsü`} sub="milyon USD" className="print-narrow"><Bridge unit="M USD" steps={[{l:'FVÖK',v:lastU.ebit,total:true},{l:'Amortisman',v:lastU.da},{l:'Geri ekleme',v:lastU.addb},...(st.fxInEbitda?[{l:'Kur farkı',v:lastU.ebitdaAdj-lastU.ebitda-lastU.addb}]:[]),{l:'Normalize FAVÖK',v:lastU.ebitdaAdj,total:true}]} /></Panel>
    </div>
    <Panel title="Maliyet yapısı (7/A)" sub={unit} flush className="print-narrow">
      <FinTable head={head} rows={[
        R('Direkt ilk madde ve malzeme (710)', h=>h.is.cost7.mat, 'kid'),
        R('Direkt işçilik (720)', h=>h.is.cost7.lab, 'kid'),
        R('Genel üretim giderleri (730)', h=>h.is.cost7.ovh, 'kid'),
        R('Bunun amortisman kısmı', h=>h.is.da.cogs, 'dim'),
        R('7/A üretim maliyetleri toplamı', h=>h.is.cost7.mat+h.is.cost7.lab+h.is.cost7.ovh, 'sub'),
        R('62x satışların maliyeti', h=>h.is.cogs, 'sub'),
        R('Fark: stok değişimi, ticari mal maliyeti', h=>h.is.cogs-(h.is.cost7.mat+h.is.cost7.lab+h.is.cost7.ovh), 'dim'),
      ]} fmt={fmt}
        note="Projeksiyonda satışların maliyeti bu karmaya göre malzeme, işçilik ve genel üretim olarak bölünür." />
    </Panel>
    {recon.length>0 && <Panel title="Satış raporu mutabakatı" sub="bin TL" flush>
      <FinTable head={recon.map(r=>({l:String(r.y)}))} rows={[
        { l:'Aylık satış raporu cirosu', v: recon.map(r=>r.rep/1000) },
        { l:'Mizan net satışları', v: recon.map(r=>r.miz/1000) },
        { l:'Fark', v: recon.map(r=>(r.rep-r.miz)/1000), cls:'sub' },
        { l:'Fark / mizan', v: recon.map(r=>(r.rep-r.miz)/r.miz), f:pc, cls:'dim' },
        { l:'Rapordaki hammadde', v: recon.map(r=>r.mat/1000) },
        { l:'Mizan 710 direkt malzeme', v: recon.map(r=>r.mat7/1000) },
      ]} note="Değerleme mizanı esas alır. Fark iade ve iskontolar, KDV, grup içi satışlar ya da dönemsellikten kaynaklanabilir; mutabakat yapılmadan rapor rakamı kullanılmamalıdır." />
    </Panel>}
    <Panel title="Katkı seviyeleri" sub={`${H.year}, gider alt hesaplarından`} right={!print && <Seg small value={H.year} onChange={setCy} options={hist.map(h=>({v:h.year,l:String(h.year)}))} />} flush className="print-narrow">
      {ct.rows.length ? (<>
        <div className="kband">
          {[['Katkı 1','A − B',ct.k1],['Katkı 2','A − B − C',ct.k2],['Katkı 3','A − B … E',ct.k3],['Katkı 4','A − B … F',ct.k4]].map(([n,f,v])=>(
            <div key={n}><span className="kn">{n}</span><b>{pc(v/(ct.A||1))}</b><span className="kf">{f} · {nf(v/1e6,1)} M ₺</span></div>))}
        </div>
        <div className="scroll"><table className="g">
          <thead><tr className="hd solo"><th className="lbl">Gider alt hesabı</th><th className="n">bin TL</th><th className="n">% ciro</th><th>Seviye</th><th></th></tr></thead>
          <tbody>{ct.rows.map(r => (
            <tr key={r.d}><td className="lbl"><code>{r.code}</code> {r.name}</td><td className="n">{nf(r.v/1000)}</td><td className="n">{pc(r.v/(ct.A||1))}</td>
              <td><select className={'csel'+(r.auto?'':' set')} value={r.cls} onChange={e=>setCls(r.d, e.target.value)}>{Object.entries(LEVELS).map(([k,n])=><option key={k} value={k}>{k} · {n}</option>)}</select></td>
              <td>{r.d!=='GAP' && <button type="button" className="lnk" disabled={savingCls} onClick={()=>saveClassifications({ valuationTaskId: st.valuationTaskId, year: H.year, items: [{ code: r.code, contributionClass: r.cls, isManual: true }] })}>Kaydet</button>}</td></tr>))}</tbody>
          <tfoot><tr><td colSpan="4">A net satışlardır. Sınıflama ad anahtarlarıyla önerilir, her satır değiştirilebilir. 7/A tutarları stok değişimi nedeniyle 62x maliyetten farklı olabilir; seviyeler yönetsel analiz içindir.</td></tr></tfoot>
        </table></div>
      </>) : <Empty title="Alt hesap yok">Katkı seviyeleri için 7'li hesapların alt kırılımlarını içeren detay mizan gerekir. Beyannamede bu kırılım bulunmaz.</Empty>}
    </Panel>
    </div>
  </>);
  if (print) return <div className="left">{body}</div>;

  const modal = modalKey && modalKey.codes && (
    <div style={{ position:'fixed', inset:0, background:'rgba(17,28,51,.55)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center' }} onClick={()=>setModalKey(null)}>
      <div style={{ background:'#fff', borderRadius:10, width:'min(960px, 95vw)', maxHeight:'80vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,.3)', position:'relative' }} onClick={e=>e.stopPropagation()}>
        <button type="button" onClick={()=>setModalKey(null)} aria-label="Kapat"
          style={{ position:'absolute', top:10, right:12, border:0, background:'none', fontSize:20, lineHeight:1, cursor:'pointer', color:'var(--muted)' }}>×</button>
        <div style={{ padding:'18px 22px' }}>
          <h3 style={{ margin:'0 0 14px' }}>{modalKey.l}</h3>
          {(() => {
            const byCode = {};
            hist.forEach(h => {
              Object.values(h.L.acc).forEach(a => {
                const match = modalKey.codes.find(p => +a.code>=p.from && +a.code<=p.to);
                if (!match) return;
                (byCode[a.code] ||= { name: a.name || accName(a.code), s: match.s, vals: {} }).vals[h.year] = match.s*flow(h.L, a.code);
              });
            });
            const codes = Object.keys(byCode).sort();
            if (!codes.length) return <p className="muted small">Bu yıllarda bu satırı besleyen hesap yok.</p>;
            return (
              <table className="g"><thead><tr className="hd solo"><th className="lbl">Hesap</th>{hist.map(h=><th key={h.id} className="n">{h.year}</th>)}</tr></thead>
                <tbody>
                  {codes.map(code => (
                    <tr key={code}><td className="lbl"><code>{code}</code> {byCode[code].name}</td>
                      {hist.map(h => <td key={h.id} className="n">{fmt(c(h, byCode[code].vals[h.year] ?? 0))}</td>)}
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr className="sum"><td className="lbl">Toplam</td>
                  {hist.map(h => <td key={h.id} className="n">{fmt(c(h, codes.reduce((s,c2)=>s+(byCode[c2].vals[h.year]??0),0)))}</td>)}
                </tr></tfoot>
              </table>
            );
          })()}
          {modalKey.amort && (() => {
            const SRC = { sub:'alt hesaplar', delta:'birikmiş fark', pct:'%4 tahmin', manual:'elle giriş' };
            const daVal = r => { const net=(+r.db||0)-(+r.cb||0); return Math.abs(net)>0.5 ? net : (+r.dt||0); };
            const byCode = {};
            hist.forEach(h => {
              if (h.is.da.source==='sub' && h.is.da.rows?.length) {
                h.is.da.rows.forEach(r => { (byCode[r.code] ||= { name:r.name, vals:{} }).vals[h.year] = daVal(r); });
              } else {
                (byCode['_est_'+h.year] ||= { name:`Amortisman ve itfa (${SRC[h.is.da.source]||h.is.da.source})`, vals:{} }).vals[h.year] = h.is.da.total;
              }
            });
            const codes = Object.keys(byCode).sort();
            return (
              <div style={{ marginTop:18 }}>
                <h4 style={{ margin:'0 0 8px', fontSize:12.5 }}>Amortisman ve itfa</h4>
                <table className="g"><thead><tr className="hd solo"><th className="lbl">Kaynak</th>{hist.map(h=><th key={h.id} className="n">{h.year}</th>)}</tr></thead>
                  <tbody>
                    {codes.map(code => (
                      <tr key={code}><td className="lbl">{code.startsWith('_est_') ? '' : <code>{code}</code>} {byCode[code].name}</td>
                        {hist.map(h => <td key={h.id} className="n">{(byCode[code].vals[h.year]??0)===0 ? '—' : fmt(c(h, byCode[code].vals[h.year]))}</td>)}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr className="sum"><td className="lbl">Amortisman toplamı</td>
                    {hist.map(h => <td key={h.id} className="n">{fmt(c(h, h.is.da.total))}</td>)}
                  </tr></tfoot>
                </table>
                <table className="g" style={{ marginTop:10 }}><tbody>
                  <tr className="hero"><td className="lbl">FAVÖK (FVÖK + Amortisman)</td>
                    {hist.map(h => <td key={h.id} className="n">{fmt(c(h, h.is.ebitda))}</td>)}
                  </tr>
                </tbody></table>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );

  const addAb = () => setAbDraft({ description:'', year: hist[hist.length-1].year, tl:0, currency:'TRY', ev:false, on:true });
  const persist = list => saveAddbacksMut({
    valuationTaskId: st.valuationTaskId,
    rows: list.map(a => ({ name: a.description || '', description: a.description || '', year: a.year, amount: a.tl, currency: a.currency || 'TRY', hasEvidence: !!a.ev, isActive: !!a.on })),
  }).unwrap().then(saved => up({ addbacks: saved.map(a => ({ id: a.id, description: a.description || a.name || '', year: a.year, tl: a.amount, currency: a.currency, ev: a.hasEvidence, on: a.isActive })) }));
  const saveAbDraft = () => {
    const next = abDraft.id ? st.addbacks.map(a => a.id===abDraft.id ? abDraft : a) : [...st.addbacks, abDraft];
    persist(next).then(() => setAbDraft(null)).catch(() => {});
  };
  const deleteAbDraft = () => {
    if (!abDraft.id) { setAbDraft(null); return; }
    persist(st.addbacks.filter(a => a.id!==abDraft.id)).then(() => setAbDraft(null)).catch(() => {});
  };
  return (<>
    <div className="left">{body}</div>
    {modal}
    <Insp>
      <div className="ihead">Uyarlamalar <span>L4</span></div>
      <div className="iblk"><h4>Amortisman (bin TL)</h4>
        {hist.map(h => (
          <div className="irow da-row" key={h.year}>
            <span className="yr">{h.year}</span>
            <Tag c={h.is.da.source==='sub'?'green':h.is.da.source==='manual'?'teal':'gold'}>{({sub:'alt hesap',delta:'birikmiş fark',pct:'%4 tahmin',manual:'elle'})[h.is.da.source]}</Tag>
            <input className="ninp w" defaultValue={nf(h.is.da.total/1000)} key={h.year+':'+Math.round(h.is.da.total)}
              onChange={()=>setDaTouched(true)}
              onBlur={e=>{ const t=e.target.value.trim(); const v=parseFloat(t.replace(/\./g,'').replace(',','.')); up(s=>({ daOverride: { ...s.daOverride, [h.year]: t==='' ? null : (isFinite(v)? v*1000 : null) } })); }} />
          </div>))}
        <div style={{display:'flex',gap:8,marginTop:10}}>
          <button className="ibtn" style={{flex:1,marginTop:0,background:'var(--teal)',borderColor:'var(--teal)'}} disabled={!daTouched && !Object.values(st.daOverride||{}).some(v=>v!=null)} onClick={()=>setDaConfirm(true)}>Geri al</button>
          <button className="ibtn" style={{flex:1,marginTop:0}} disabled={!st.valuationTaskId || !daTouched} onClick={()=>{ saveDaOverrideMut({ valuationTaskId: st.valuationTaskId, rows: Object.entries(st.daOverride||{}).filter(([,v])=>v!=null).map(([year,amount])=>({ year:+year, amount })) }); setDaTouched(false); }}>Kaydet</button>
        </div>
      </div>
      <div className="iblk"><h4>Geri eklemeler{st.addbacks.length>0 && <span className="ab-chip" style={{marginLeft:6}}>{st.addbacks.length}</span>}</h4>
        {st.addbacks.length===0 && <p className="muted small">Tek seferlik, ortak kaynaklı ya da piyasa dışı giderler FAVÖK'e geri eklenebilir. Kanıtsız kalemler güven skorunu düşürür.</p>}
        {st.addbacks.length>0 && <div className="ab-items">
          {st.addbacks.map(a => (
            <div key={a.id} className={'ab-it'+(a.on?'':' off')+(abDraft?.id===a.id?' sel':'')} onClick={()=>setAbDraft(abDraft?.id===a.id?null:{...a})}>
              <div className="tx">
                <div className="ab-row1">
                  <div className="nm" onMouseEnter={e=>{ const r=e.currentTarget.getBoundingClientRect(); setAbHint({ text: a.description||'İsimsiz', x: r.left, y: r.top }); }} onMouseLeave={()=>setAbHint(null)}>{a.description||'İsimsiz'}</div>
                  <span className="am">+{nf(a.tl/1000)} bin {a.currency==='USD'?'$':'₺'}</span>
                </div>
                <div className="mt">
                  <span className="ab-chip">{a.year}</span>
                  <span className={'ab-chip'+(a.on?' on':'')}>{a.on?'uygulandı':'uygulanmıyor'}</span>
                  <span className={'ab-chip'+(a.ev?' on':'')}>{a.ev?'kanıtlı':'kanıtsız'}</span>
                </div>
              </div>
            </div>))}
        </div>}
        {abDraft && (
          <div className="ab-form">
            <div className="ab-fh"><b>Geri ekleme</b><button type="button" className="ab-del" title="Kapat" onClick={()=>setAbDraft(null)}>✕</button></div>
            <input className="ab-fld t" value={abDraft.description||''} onChange={e=>setAbDraft(d=>({...d,description:e.target.value}))} placeholder="Açıklama" aria-label="Açıklama" />
            <div className="ab-row">
              <select className="ab-fld" value={abDraft.year} onChange={e=>setAbDraft(d=>({...d,year:+e.target.value}))}>{hist.map(h=><option key={h.year}>{h.year}</option>)}</select>
              <label className="ab-amt"><input defaultValue={nf(abDraft.tl/1000)} key={(abDraft.id||'new')+abDraft.tl} onBlur={e=>{ const v=parseFloat(e.target.value.replace(/\./g,'').replace(',','.')); setAbDraft(d=>({...d,tl: isFinite(v)? v*1000 : 0})); }} aria-label="Tutar" /></label>
              <select className="ab-fld" value={abDraft.currency||'TRY'} onChange={e=>setAbDraft(d=>({...d,currency:e.target.value}))} aria-label="Para birimi">
                <option value="TRY">bin ₺</option>
                <option value="USD">bin $</option>
              </select>
            </div>
            <div className="ab-opts">
              <label className="ab-cb"><input type="checkbox" checked={abDraft.on} onChange={e=>setAbDraft(d=>({...d,on:e.target.checked}))}/>Uygula</label>
              <label className="ab-cb"><input type="checkbox" checked={abDraft.ev} onChange={e=>setAbDraft(d=>({...d,ev:e.target.checked}))}/>Kanıtlı</label>
            </div>
            <div style={{display:'flex',gap:8}}>
              <button className="ibtn" style={{flex:1,marginTop:0}} disabled={savingAb || !st.valuationTaskId} onClick={saveAbDraft}>{savingAb?'Kaydediliyor…':'Kaydet'}</button>
              {abDraft.id && <button className="ibtn" style={{flex:1,marginTop:0,background:'var(--teal)',borderColor:'var(--teal)'}} disabled={savingAb} onClick={deleteAbDraft}>Sil</button>}
            </div>
          </div>
        )}
        {!abDraft && <button type="button" className="ab-add" onClick={addAb}>+ Geri ekleme ekle</button>}
      </div>
      <div className="iblk"><h4>Normalizasyon</h4>
        <Toggle checked={st.fxInEbitda} onChange={v=>up({fxInEbitda:v})} title="Net kur farkını FAVÖK'e kat" desc="İhracatçı firmalarda kur farkı faaliyetle ilgili olabilir. Varsayılan olarak hariç tutulur." />
        {hist.filter(h=>h.months<12).map(h => (
          <NumField key={h.id} label={`${h.year} yıllıklandırma`} value={h.annualize} step={0.05} digits={2} unit="x" min={1} max={12} onChange={v=>up(s=>({annualize:{...s.annualize,[h.id]:v}}))} />))}
      </div>
      <div className="inote"><b>Taban:</b> üç yöntem de bu normalize FAVÖK'ten beslenir. Taban yanlışsa üç sonuç birden yanlış çıkar ve birbirini doğruluyor gibi görünür.</div>
    </Insp>
    {abHint && (
      <div style={{ position:'fixed', top:abHint.y, left:abHint.x, transform:'translate(-100%,-50%)', marginLeft:-10, background:'var(--navy)', color:'#fff', padding:'8px 11px', borderRadius:7, fontSize:12, lineHeight:1.4, maxWidth:260, boxShadow:'0 8px 24px rgba(20,33,61,.25)', zIndex:500, pointerEvents:'none' }}>
        {abHint.text}
      </div>
    )}
    {daConfirm && (
      <div style={{ position:'fixed', inset:0, background:'rgba(17,28,51,.55)', zIndex:300, display:'flex', alignItems:'center', justifyContent:'center' }} onClick={()=>setDaConfirm(false)}>
        <div style={{ background:'#fff', borderRadius:10, width:'min(380px, 92vw)', boxShadow:'0 20px 60px rgba(0,0,0,.3)', padding:'20px 22px' }} onClick={e=>e.stopPropagation()}>
          <h3 style={{ margin:'0 0 8px', fontSize:15 }}>Amortisman girişlerini sil</h3>
          <p className="muted small" style={{ margin:'0 0 18px' }}>Tüm manuel amortisman değişiklikleri silinsin mi? Bu işlem geri alınamaz.</p>
          <div style={{ display:'flex', gap:8 }}>
            <button className="ibtn ghost" style={{ flex:1, marginTop:0 }} onClick={()=>setDaConfirm(false)}>Vazgeç</button>
            <button className="ibtn" style={{ flex:1, marginTop:0, background:'var(--red)', borderColor:'var(--red)' }}
              onClick={()=>{ up({daOverride:{}}); saveDaOverrideMut({ valuationTaskId: st.valuationTaskId, rows: [] }); setDaTouched(false); setDaConfirm(false); }}>Sil</button>
          </div>
        </div>
      </div>
    )}
  </>);
}
